// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/ConditionalNode.js (393 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react; Handle/Position/useReactFlow/
 * useUpdateNodeInternals API identical across libs. Conditions JSON
 * (variableId/operatorId/variableType/value + ancestor 'av:' variants)
 * preserved verbatim.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Table } from './_rbShim';
import {
  Handle,
  Position,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import {
  GitBranch,
  Settings,
  Trash2,
  Plus,
  Check,
  ArrowRight,
  ArrowDown,
} from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import { NodesContext } from '../provider/IvrContext';
import { MAX_CONDITIONS } from '@/utilities/constant';

const VARIABLE_TYPES = ['Number', 'Boolean', 'Text'];

const generateRowId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const emptyRow = () => ({
  rowId: generateRowId(),
  variableId: '',
  variableName: '',
  variableValue: '',
  operatorId: '',
  operatorLabel: '',
  operatorValue: '',
  variableType: '',
  value: '',
});

const ConditionalNode = ({ data, id }: any) => {
  const {
    flowVariablesList = [],
    flowOperatorsList = [],
    updateNodeData,
    onRemove,
  } = useContext(NodesContext) as any;
  const { getNodes, getEdges } = useReactFlow();

  const { conditions = [] } = data;

  // Walk upstream through the flow graph collecting variables earlier nodes
  // made available: DTMF's "make collected digits available" and API Webhook's
  // "play in next node" response params — same logic ApiNode.js uses for its
  // own custom-params picker, so both node types' future variables show up here.
  const getAncestorVariables = () => {
    const vars: any[] = [];
    const seenNames = new Set();
    let currentId = id;

    while (true) {
      const incomingEdge = getEdges().find((e: any) => e.target === currentId);
      if (!incomingEdge) break;
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      if (!parentNode) break;

      if (
        Array.isArray(parentNode?.data?.availableVariables) &&
        parentNode.data.availableVariables.length > 0
      ) {
        parentNode.data.availableVariables.forEach((v: any) => {
          if (!seenNames.has(v.name)) {
            seenNames.add(v.name);
            vars.push(v);
          }
        });
      }

      if (
        (parentNode?.type === 'apiNode' || parentNode?.data?.verb === 'api') &&
        Array.isArray(parentNode?.data?.action?.selected_available_params)
      ) {
        parentNode.data.action.selected_available_params.forEach(
          (param: any) => {
            const varName = `{${param}}`;
            if (!seenNames.has(varName)) {
              seenNames.add(varName);
              vars.push({ name: varName, nodeId: parentNode.id, enable: false });
            }
          },
        );
      }

      currentId = parentNode.id;
    }

    return vars;
  };

  // The If/Else handle offsets are fixed in this component, but React Flow only
  // re-measures a node's handle positions when its overall bounding box size
  // changes — it has no way to know two static-looking handles moved without
  // the node itself resizing. Force a re-measure on mount so edges always
  // anchor to where the dots are actually drawn (matters most right after a
  // handle layout change, or when a flow saved under an older layout loads).
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, updateNodeInternals]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [draftConditions, setDraftConditions] = useState<any[]>(conditions);
  const [errors, setErrors] = useState<any>({});
  const [ancestorVars, setAncestorVars] = useState<any[]>([]);

  const openModal = () => {
    const seeded = conditions.length
      ? JSON.parse(JSON.stringify(conditions)).map((row: any) => ({
          ...row,
          rowId: row.rowId || generateRowId(),
        }))
      : [emptyRow()];
    setDraftConditions(seeded);
    setAncestorVars(getAncestorVariables());
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setDraftConditions(conditions);
    setErrors({});
    setModalIsOpen(false);
  };

  const updateRow = (index: number, updates: any) => {
    setDraftConditions((prev) =>
      prev.map((row: any, i: number) =>
        i === index ? { ...row, ...updates } : row,
      ),
    );
  };

  const handleVariableChange = (index: number, variableId: string) => {
    if (variableId.startsWith('av:')) {
      const selected = ancestorVars.find(
        (v: any) => `av:${v.nodeId}:${v.name}` === variableId,
      );
      updateRow(index, {
        variableId,
        variableName: selected?.name || '',
        variableValue: selected?.name || '',
        operatorId: '',
        operatorLabel: '',
        operatorValue: '',
      });
      return;
    }
    const selected = flowVariablesList.find(
      (v: any) => String(v.id) === variableId,
    );
    updateRow(index, {
      variableId,
      variableName: selected?.name || '',
      variableValue: selected?.value || '',
      operatorId: '',
      operatorLabel: '',
      operatorValue: '',
    });
  };

  const handleOperatorChange = (index: number, operatorId: string) => {
    const selected = flowOperatorsList.find(
      (o: any) => String(o.id) === operatorId,
    );
    updateRow(index, {
      operatorId,
      operatorLabel: selected?.name || '',
      operatorValue: selected?.value || '',
    });
  };

  const handleTypeChange = (index: number, variableType: string) => {
    updateRow(index, { variableType, value: '' });
  };

  const addRow = () => {
    setDraftConditions((prev) =>
      prev.length >= MAX_CONDITIONS ? prev : [...prev, emptyRow()],
    );
    setErrors({});
  };

  const removeRow = (index: number) => {
    setDraftConditions((prev) => prev.filter((_: any, i: number) => i !== index));
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (draftConditions.length > MAX_CONDITIONS) {
      setErrors({
        limit: `A conditional node can have at most ${MAX_CONDITIONS} conditions`,
      });
      return false;
    }
    draftConditions.forEach((row: any, i: number) => {
      if (
        !row.variableId ||
        !row.operatorId ||
        !row.variableType ||
        row.value === ''
      ) {
        newErrors[i] = 'Complete all fields for this condition';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    updateNodeData(id, { ...data, conditions: draftConditions });
    setModalIsOpen(false);
  };

  const HEAD_H = 40;
  const DIV_H = 1;
  const SUMMARY_H = 20;
  const ROW_H = 22;

  const ifTop = HEAD_H + DIV_H + SUMMARY_H + ROW_H / 2;

  return (
    <div className="ivrn" style={{ '--node-color': '#c026d3' } as any}>
      <span
        className="ivrn-icon"
        style={{
          background: '#c026d3',
          color: '#fff',
          top: 'calc(30% - 10px)',
        }}
      >
        <GitBranch size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={Position.Left}
        id={id}
        style={{ background: '#c026d3' }}
      />

      <Handle
        type="source"
        position={Position.Right}
        id={`${id}_if`}
        style={{
          top: ifTop,
          backgroundColor: '#16a34a',
          width: 8,
          height: 8,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id={`${id}_else`}
        style={{ backgroundColor: '#c026d3', width: 8, height: 8 }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Conditional</span>
        {conditions.length > 0 && (
          <span
            className="ivrn-badge"
            style={{ background: '#f5d0fe', color: '#86198f' }}
          >
            {conditions.length}
          </span>
        )}
      </div>
      <div className="ivrn-divider" />
      <div className="ivrn-body" style={{ padding: 0 }}>
        <div
          style={{
            height: SUMMARY_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px 0 96px',
          }}
        >
          {conditions.length > 0 && (
            <span className="ivrn-val" style={{ fontSize: 13 }}>
              {conditions.length} condition
              {conditions.length !== 1 ? 's' : ''} (AND)
            </span>
          )}
        </div>
        <div
          style={{
            height: ROW_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px 0 96px',
            fontSize: 13,
            fontWeight: 600,
            borderTop: '1px solid #f2f4f7',
            color: '#166534',
          }}
        >
          <Check
            size={11}
            style={{ marginRight: 4, color: '#16a34a', flexShrink: 0 }}
          />
          If (all match)
          <ArrowRight
            size={16}
            style={{ marginLeft: 6, color: '#16a34a', flexShrink: 0 }}
          />
        </div>
        <div
          style={{
            height: ROW_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px 0 96px',
            fontSize: 13,
            fontWeight: 600,
            borderTop: '1px solid #f2f4f7',
            color: '#86198f',
          }}
        >
          Else
          <ArrowDown
            size={16}
            style={{ marginLeft: 6, color: '#c026d3', flexShrink: 0 }}
          />
        </div>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Conditional"
        subtitle="All conditions are AND'd together — 'If' fires when every row matches, otherwise 'Else' fires."
        width="820px"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Discard
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Conditions</div>
          <div style={{ overflowX: 'auto' }}>
            <style>{`
              .ivr-cond-table th, .ivr-cond-table td {
                padding: 10px 10px;
                vertical-align: middle;
              }
              .ivr-cond-table tbody tr:not(:last-child) td {
                border-bottom: 1px solid hsl(var(--border) / 0.5);
              }
            `}</style>
            <Table
              style={{ minWidth: 700, marginBottom: 0 }}
              className="align-middle ivr-cond-table"
            >
              <thead>
                <tr>
                  <th style={{ width: '26%' }}>Variable</th>
                  <th style={{ width: '22%' }}>Operator</th>
                  <th style={{ width: '18%' }}>Variable Type</th>
                  <th style={{ width: '26%' }}>Value</th>
                  <th style={{ width: '8%' }} />
                </tr>
              </thead>
              <tbody>
                {draftConditions.map((row: any, i: number) => (
                  <tr key={row.rowId || i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.variableId}
                        isInvalid={!!errors[i] && !row.variableId}
                        onChange={(e) =>
                          handleVariableChange(i, e.target.value)
                        }
                      >
                        <option value="">Select variable</option>
                        {flowVariablesList.length > 0 && (
                          <optgroup label="Flow Variables">
                            {flowVariablesList.map((v: any) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {ancestorVars.length > 0 && (
                          <optgroup label="From Previous Nodes">
                            {ancestorVars.map((v: any, j: number) => (
                              <option
                                key={`${v.nodeId}-${j}`}
                                value={`av:${v.nodeId}:${v.name}`}
                              >
                                {v.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.operatorId}
                        disabled={!row.variableId}
                        isInvalid={!!errors[i] && !row.operatorId}
                        onChange={(e) =>
                          handleOperatorChange(i, e.target.value)
                        }
                      >
                        <option value="">Select operator</option>
                        {flowOperatorsList.map((o: any) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.variableType}
                        isInvalid={!!errors[i] && !row.variableType}
                        onChange={(e) =>
                          handleTypeChange(i, e.target.value)
                        }
                      >
                        <option value="">Select type</option>
                        {VARIABLE_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      {row.variableType === 'Boolean' ? (
                        <Form.Select
                          size="sm"
                          value={row.value}
                          isInvalid={!!errors[i] && row.value === ''}
                          onChange={(e) =>
                            updateRow(i, { value: e.target.value })
                          }
                        >
                          <option value="">Select value</option>
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </Form.Select>
                      ) : row.variableType === 'Number' ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={row.value}
                          isInvalid={!!errors[i] && row.value === ''}
                          onChange={(e) =>
                            updateRow(i, { value: e.target.value })
                          }
                        />
                      ) : (
                        <Form.Control
                          size="sm"
                          type="text"
                          value={row.value}
                          disabled={!row.variableType}
                          isInvalid={!!errors[i] && row.value === ''}
                          onChange={(e) =>
                            updateRow(i, { value: e.target.value })
                          }
                        />
                      )}
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="ivrn-btn ivrn-btn--del"
                        title="Remove condition"
                        onClick={() => removeRow(i)}
                      >
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {Object.keys(errors).length > 0 && (
            <Form.Text
              className="text-danger d-block"
              style={{ marginTop: 8 }}
            >
              {errors.limit ||
                'Please complete all fields for every condition.'}
            </Form.Text>
          )}

          <div
            style={{
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={addRow}
              disabled={draftConditions.length >= MAX_CONDITIONS}
              title={
                draftConditions.length >= MAX_CONDITIONS
                  ? `Maximum ${MAX_CONDITIONS} conditions allowed`
                  : 'Add condition'
              }
            >
              <Plus size={14} strokeWidth={2} style={{ marginRight: 4 }} />
              Add Condition
            </Button>
            <span
              style={{
                fontSize: 12,
                color:
                  draftConditions.length >= MAX_CONDITIONS
                    ? '#b91c1c'
                    : '#6b7280',
              }}
            >
              {draftConditions.length} / {MAX_CONDITIONS} conditions
            </span>
          </div>

          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: '#6b7280',
              background: '#faf5ff',
              border: '1px solid #f3e8ff',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            All rows above are combined with <strong>AND</strong>. On the
            canvas, connect the{' '}
            <strong style={{ color: '#16a34a' }}>If</strong> output when every
            condition should match, and the{' '}
            <strong style={{ color: '#86198f' }}>Else</strong> output for when
            any condition fails.
          </div>
        </div>
      </IvrDrawer>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default ConditionalNode;
